import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

const OUR_WORK_API_URL = "/api/filter/ourwork?page=1&limit=20";
const HERO_IMAGE = "/work.webp";

const isExternalUrl = (url = "") => /^https?:\/\//i.test(String(url));

const getTextValue = (value) => {
  if (value === null || value === undefined) return "";

  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }

  if (typeof value === "object") {
    return String(
      value.label ||
        value.name ||
        value.title ||
        value.value ||
        value.text ||
        ""
    ).trim();
  }

  return "";
};

const toLabelArray = (value) => {
  if (Array.isArray(value)) {
    return value.map(getTextValue).filter(Boolean);
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        return parsed.map(getTextValue).filter(Boolean);
      }
    } catch {
      // fallback to comma split
    }

    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (value && typeof value === "object") {
    const singleValue = getTextValue(value);
    return singleValue ? [singleValue] : [];
  }

  return [];
};

const getApiList = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.data)) return result.data.data;
  if (Array.isArray(result?.data?.ourwork)) return result.data.ourwork;
  if (Array.isArray(result?.data?.ourWork)) return result.data.ourWork;
  if (Array.isArray(result?.data?.projects)) return result.data.projects;
  if (Array.isArray(result?.data?.items)) return result.data.items;
  if (Array.isArray(result?.ourwork)) return result.ourwork;
  if (Array.isArray(result?.ourWork)) return result.ourWork;
  if (Array.isArray(result?.projects)) return result.projects;
  if (Array.isArray(result?.items)) return result.items;

  return [];
};

const getServiceRoute = (value = "") => {
  const text = String(value).toLowerCase();

  if (text.includes("website")) return "/services/website-development";
  if (text.includes("seo")) return "/services/seo-services";
  if (text.includes("social")) return "/services/social-media-marketing";
  if (text.includes("maintenance")) return "/services/maintenance-support";

  if (
    text.includes("custom") ||
    text.includes("tool") ||
    text.includes("dashboard") ||
    text.includes("calculator")
  ) {
    return "/services/custom-tool-creation";
  }

  return "/services";
};

const normalizeSameDomainUrl = (url = "") => {
  const cleanUrl = getTextValue(url);

  if (!cleanUrl) return "/services";

  if (!isExternalUrl(cleanUrl)) {
    return cleanUrl;
  }

  try {
    const parsedUrl = new URL(cleanUrl);

    if (
      typeof window !== "undefined" &&
      parsedUrl.hostname === window.location.hostname
    ) {
      return `${parsedUrl.pathname}${parsedUrl.search || ""}`;
    }

    return cleanUrl;
  } catch {
    return "/services";
  }
};

const normalizeProject = (item = {}) => {
  const category =
    getTextValue(
      item.sub_title ||
        item.category ||
        item.service_category ||
        item.serviceCategory ||
        item.service ||
        item.service_name ||
        item.serviceName ||
        item.project_category ||
        item.projectCategory ||
        item.type
    ) || "Our Work";

  const title =
    getTextValue(
      item.title ||
        item.project_title ||
        item.projectTitle ||
        item.name ||
        item.project_name ||
        item.projectName
    ) || "Project";

  const client =
    getTextValue(
      item.client_name ||
        item.clientName ||
        item.client ||
        item.client_type ||
        item.clientType ||
        item.industry ||
        item.company_name ||
        item.companyName
    ) || "Client";

    const clientLogo = getTextValue(
  item.client_logo ||
    item.clientLogo ||
    item.logo ||
    item.client_image ||
    item.clientImage ||
    item.image ||
    item.image_url ||
    item.imageUrl
);

  const description = getTextValue(
    item.description ||
      item.project_description ||
      item.projectDescription ||
      item.details ||
      item.short_description ||
      item.shortDescription ||
      item.summary
  );

  const proofBoxes = toLabelArray(
    item.highlights ||
      item.proofBoxes ||
      item.proof_boxes ||
      item.proofBox ||
      item.results ||
      item.features ||
      item.key_points ||
      item.keyPoints
  );

  const tags = toLabelArray(
    item.tags ||
      item.tag ||
      item.services ||
      item.keywords ||
      item.skills ||
      item.technologies
  );

  const relatedService =
    getTextValue(item.related_service || item.relatedService) || category;

  const relatedServiceUrl =
    getTextValue(
      item.related_service_url ||
        item.relatedServiceUrl ||
        item.primaryServiceLink ||
        item.primary_service_link ||
        item.service_link ||
        item.serviceLink ||
        item.link
    ) || getServiceRoute(relatedService);

  const normalizedLink = normalizeSameDomainUrl(relatedServiceUrl);

  return {
  category,
  title,
  titleLink: normalizedLink,
  client,
  clientLogo,
  description,
  proofBoxes,
  tags,
  primaryServiceText: relatedService
    ? `Related Service: ${relatedService}`
    : "View Service",
  primaryServiceLink: normalizedLink,
};
};

function SmartLink({ to = "/services", className = "", children }) {
  if (isExternalUrl(to)) {
    return (
      <a
        href={to}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function SmartButtonLink({ to = "/services", children }) {
  if (isExternalUrl(to)) {
    return (
      <Button
        variant="gradient"
        className="justify-center sm:justify-start"
        asChild
      >
        <a href={to} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      </Button>
    );
  }

  return (
    <Button
      variant="gradient"
      className="justify-center sm:justify-start"
      asChild
    >
      <Link to={to}>{children}</Link>
    </Button>
  );
}

function ProjectCard({ project, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: index * 0.08 }}
      className="group relative h-full overflow-hidden rounded-[28px] border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-medium sm:p-7 lg:p-8"
    >
      <div className="pointer-events-none absolute -right-16 -top-16 h-44 w-44 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-44 w-44 rounded-full bg-secondary/10 blur-3xl" />

      <div className="relative z-10 flex h-full flex-col">
  <div className="mb-5 flex items-start justify-between gap-4">
    <div className="flex flex-wrap items-center gap-3 pt-1">
      <Badge className="rounded-full border-none bg-primary/10 px-3 py-1.5 text-primary">
        {project.category}
      </Badge>

      {project.client ? (
        <span className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-muted-foreground">
          {project.client}
        </span>
      ) : null}
    </div>

    {project.clientLogo ? (
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-white p-1 shadow-soft">
        <img
          src={project.clientLogo}
          alt={`${project.client || project.title} logo`}
          className="h-full w-full object-contain"
          loading="lazy"
          onError={(event) => {
            event.currentTarget.closest("div").style.display = "none";
          }}
        />
      </div>
    ) : null}
  </div>

        {/* <SmartLink to={project.titleLink || "/services"} className="group/title">
          <h3 className="text-2xl font-display font-bold leading-tight text-foreground transition-colors group-hover/title:text-primary sm:text-3xl">
            {project.title}
          </h3>
        </SmartLink> */}
        <h3 className="text-2xl font-display font-bold leading-tight text-foreground sm:text-3xl">
  {project.title}
</h3>

        {project.description ? (
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
            {project.description}
          </p>
        ) : null}

        {project.proofBoxes.length > 0 ? (
          <div className="mt-7 grid gap-3 sm:grid-cols-2">
            {project.proofBoxes.slice(0, 6).map((item, itemIndex) => (
              <div
                key={`${item}-${itemIndex}`}
                className="rounded-2xl border border-border bg-background/70 px-4 py-3 text-sm font-bold text-foreground shadow-soft"
              >
                {item}
              </div>
            ))}
          </div>
        ) : null}

        {project.tags.length > 0 ? (
          <div className="mt-6 flex flex-wrap gap-2">
            {project.tags.slice(0, 6).map((tag, tagIndex) => (
              <span
                key={`${tag}-${tagIndex}`}
                className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1.5 text-xs font-semibold text-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-auto pt-8">
          <SmartButtonLink to={project.primaryServiceLink || "/services"}>
            <span className="inline-flex items-center gap-2">
              {project.primaryServiceText || "View Service"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </span>
          </SmartButtonLink>
        </div>
      </div>
    </motion.article>
  );
}

export default function WorkPage() {
  const [projects, setProjects] = useState([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectError, setProjectError] = useState("");

  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoadingProjects(true);
      setProjectError("");

      try {
        const response = await fetch(OUR_WORK_API_URL, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        let result = null;

        try {
          result = await response.json();
        } catch {
          result = null;
        }

        if (!response.ok || result?.success === false) {
          throw new Error(result?.message || "Unable to load projects.");
        }

        const apiProjects = getApiList(result);

        const normalizedProjects = apiProjects
          .map(normalizeProject)
          .filter((project) => project.title && project.title !== "Project");

        setProjects(normalizedProjects);
      } catch (error) {
        console.error("Our work API error:", error);
        setProjects([]);
        setProjectError(
          error?.message || "Projects could not be loaded right now."
        );
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  return (
    <Layout>
      <SEO
        title="Our Work | Square Trigger"
        description="Explore Square Trigger projects across website development, SEO, social media marketing, and custom tool creation for businesses that need practical digital solutions."
        ogTitle="Our Work | Square Trigger"
        ogDescription="Explore Square Trigger projects across website development, SEO, social media marketing, and custom tool creation for businesses that need practical digital solutions."
      />

      {/* Hero */}
     <section className="relative overflow-hidden pt-24 pb-16 sm:pt-28 sm:pb-18 lg:pt-32 lg:pb-20">
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-br from-foreground/80 via-foreground/70 to-foreground/60" />
  </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                Our Work
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Real Results from{" "}
              <span className="gradient-text">Real Projects</span>
            </motion.h1>

           <motion.p
  initial={{ opacity: 0, y: 30 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.6, delay: 0.2 }}
  className="mx-auto mb-8 max-w-2xl text-center text-lg sm:text-xl leading-relaxed text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
>
  Explore selected Square Trigger projects and see how we solve real
  business problems through websites, SEO, social media strategy,
  and custom tools.
</motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/services">View Our Services</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Project Cards - API DATA ONLY */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute left-10 top-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-10 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="mx-auto mb-10 max-w-[860px] text-center">
            <Badge className="mb-4 rounded-full border-none bg-primary/10 px-4 py-2 text-primary">
              Featured Projects
            </Badge>

            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              Selected Work from{" "}
              <span className="gradient-text">Our Portfolio</span>
            </h2>

           
          </div>

          {isLoadingProjects ? (
            <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-soft">
              <p className="text-base font-medium text-muted-foreground">
                Loading projects from API...
              </p>
            </div>
          ) : projectError ? (
            <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-soft">
              <h3 className="mb-2 text-xl font-display font-bold text-foreground">
                Projects could not be loaded
              </h3>
              <p className="text-base text-muted-foreground">{projectError}</p>
            </div>
          ) : projects.length > 0 ? (
            <div className="grid gap-6 lg:grid-cols-2 xl:gap-8">
              {projects.map((project, index) => (
                <ProjectCard
                  key={`${project.title}-${index}`}
                  project={project}
                  index={index}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-3xl border border-border bg-card p-8 text-center shadow-soft">
              <h3 className="mb-2 text-xl font-display font-bold text-foreground">
                No projects found
              </h3>
              <p className="text-base text-muted-foreground">
                The API returned successfully, but no project records were found.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Ready to Be Our Next Success Story?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let’s talk about the problem your business needs to solve and the
              right digital solution to build around it.
            </p>

            <Button
              variant="premium"
              size="lg"
              asChild
              className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
            >
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2"
              >
                Book a Free Consultation
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}